import React, { useState, useEffect, useRef } from 'react';
import { Block } from '../classes/Block';
import './BlockConfigForm.css';
import { t } from '@lingui/core/macro';
import {
  Select,
  MenuItem,
} from "@mui/material";
import ColorPicker from './ColorPicker';

interface FontOption {
  value: string;
  label: string;
}

interface BlockConfigFormProps {
  blockNames: string[]; // These are potential names for NEW blocks, from App.tsx's firstColValues
  onSaveBlocks: (blocks: Block[]) => void | Promise<void>;
  onBlockSelect?: (index: number) => void;
  selectedBlockIndex?: number | null;
  existingBlocks: Block[];
  fontOptions: FontOption[];
}

const BlockConfigForm: React.FC<BlockConfigFormProps> = ({ 
  blockNames, 
  onSaveBlocks, 
  onBlockSelect,
  selectedBlockIndex,
  existingBlocks,
  fontOptions
}) => {
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [activeBlockIndex, setActiveBlockIndex] = useState<number>(0);
  const [collapsedPanes, setCollapsedPanes] = useState<Record<string, boolean>>({
    layout: false,
    label: false,
    value: false
  });
  
  // Reference to track if blocks were updated from props to prevent feedback loops
  const isUpdatingFromProps = useRef<boolean>(false);

  // Synchronize the internal `blocks` state with the `existingBlocks` prop.
  // This is the primary way blocks (with dataRows) enter the form.
  useEffect(() => {
    console.log('BlockConfigForm: existingBlocks prop received:', existingBlocks);
    // Simple check: if lengths are different or object references are different for any item,
    // or if block properties relevant to the form have changed, update the internal state.
    // A more sophisticated deep comparison might be needed if props within existingBlocks can change without identity change.
    if (JSON.stringify(blocks) !== JSON.stringify(existingBlocks)) { // Basic deep compare for simplicity
      console.log('BlockConfigForm: Internal blocks are different from prop, updating internal blocks.');
      isUpdatingFromProps.current = true; // Mark that we're updating from props
      setBlocks(existingBlocks.map(b => new Block( // Create new instances to ensure no shared refs with App.tsx state directly
        b.name, b.shouldDisplayBlockName, b.blockNameFont, b.blockNameSize, b.blockNameWeight, b.blockNameColor,
        b.numColumns, b.distanceBelowBlockName, b.labelValueGap, b.marginColumn ?? 45, b.edgeDistance ?? 0, b.distanceBetweenRows, b.distanceFromPreviousBlock,
        b.isSongBlock, b.isSingleCell, b.isCreditsList, b.separator, b.isLogoBlock,
        b.labelFontName, b.labelFontSize, b.labelFontWeight, b.labelAlign, b.labelVerticalAlign, b.labelFontColor,
        b.valueFontName, b.valueFontSize, b.valueFontWeight, b.valueAlign, b.valueVerticalAlign, b.valueFontColor,
        b.hasSeparateValueFormatting || false, // Add the new property
        b.dataRows || [] // IMPORTANT: Carry over dataRows
      )));
      // If existingBlocks is empty, and we have blockNames, this might be an initial setup from App.tsx
      // where App.tsx hasn't yet processed an actual file but wants to show block tabs based on some default names.
      // However, App.tsx is now primarily responsible for creating initial blocks even from just names.
    } else {
      console.log('BlockConfigForm: Internal blocks seem identical to prop, no update.');
    }
  }, [existingBlocks]); // Only depend on existingBlocks for this effect

  // useEffect to automatically save blocks whenever they change internally
  useEffect(() => {
    // Skip saving if the update came from props to prevent feedback loop
    if (isUpdatingFromProps.current) {
      console.log('BlockConfigForm: Skipping save because update came from props');
      isUpdatingFromProps.current = false; // Reset the flag
      return;
    }

    // Only call onSaveBlocks if the blocks array reference itself has changed
    // and it's not an initial setup or an update directly from props.
    // The comparison `JSON.stringify(blocks) !== JSON.stringify(existingBlocks)` helps ensure we save
    // when internal changes make `blocks` different from the last `existingBlocks` received.
    if (JSON.stringify(blocks) !== JSON.stringify(existingBlocks)) {
        // Further check: Ensure that the current `blocks` state is not empty if `existingBlocks` wasn't, 
        // to prevent saving an accidentally cleared state. This is a safeguard.
        if (blocks.length > 0 || (existingBlocks && existingBlocks.length === 0)) {
            console.log('BlockConfigForm: Internal blocks changed, calling onSaveBlocks with:', blocks);
            const result = onSaveBlocks(blocks);
            if (result instanceof Promise) {
              result.catch(error => {
                console.error('Error in onSaveBlocks:', error);
              });
            }
        } else {
            console.log('BlockConfigForm: Internal blocks changed but are empty while existingBlocks were not; suppressing save.', blocks, existingBlocks);
        }
    }
  }, [blocks, onSaveBlocks, existingBlocks]); // Add existingBlocks here to re-evaluate save condition

  // Synchronize the internal active block index with the selected block index from parent
  useEffect(() => {
    if (selectedBlockIndex !== undefined && selectedBlockIndex !== null) {
      setActiveBlockIndex(selectedBlockIndex);
    }
  }, [selectedBlockIndex]);

  const handleBlockChange = (index: number, field: keyof Block, value: any) => {
    setBlocks(prevBlocks => {
      const updatedBlocks = prevBlocks.map((block, i) => {
        if (i === index) {
          // Handle special case for hasSeparateValueFormatting
          let newHasSeparateValueFormatting = field === 'hasSeparateValueFormatting' ? Boolean(value) : block.hasSeparateValueFormatting;
          
          // If we're disabling separate value formatting, copy label formatting to value formatting
          let newValueFontName = block.valueFontName;
          let newValueFontSize = block.valueFontSize;
          let newValueFontWeight = block.valueFontWeight;
          let newValueAlign = block.valueAlign;
          let newValueFontColor = block.valueFontColor;
          let newValueVerticalAlign = block.valueVerticalAlign;
          
          if (field === 'hasSeparateValueFormatting' && !value) {
            // Copy label formatting to value formatting
            newValueFontName = block.labelFontName;
            newValueFontSize = block.labelFontSize;
            newValueFontWeight = block.labelFontWeight;
            newValueAlign = block.labelAlign;
            newValueFontColor = block.labelFontColor;
            newValueVerticalAlign = block.labelVerticalAlign;
          } else if (field === 'labelFontName' && !block.hasSeparateValueFormatting) {
            // If separate formatting is disabled, update value formatting when label formatting changes
            newValueFontName = String(value);
          } else if (field === 'labelFontSize' && !block.hasSeparateValueFormatting) {
            newValueFontSize = Number(value);
          } else if (field === 'labelFontWeight' && !block.hasSeparateValueFormatting) {
            newValueFontWeight = Number(value);
          } else if (field === 'labelAlign' && !block.hasSeparateValueFormatting) {
            newValueAlign = String(value);
          } else if (field === 'labelFontColor' && !block.hasSeparateValueFormatting) {
            newValueFontColor = String(value);
          } else if (field === 'valueFontName') {
            newValueFontName = String(value);
          } else if (field === 'valueFontSize') {
            newValueFontSize = Number(value);
          } else if (field === 'valueFontWeight') {
            newValueFontWeight = Number(value);
          } else if (field === 'valueAlign') {
            newValueAlign = String(value);
          } else if (field === 'valueFontColor') {
            newValueFontColor = String(value);
          } else if (field === "valueVerticalAlign") {
            newValueVerticalAlign = String(value);
          }
          
          // Create a new Block instance for the modified block
          const newBlock = new Block(
            field === 'name' ? String(value) : block.name,
            field === 'shouldDisplayBlockName' ? Boolean(value) : block.shouldDisplayBlockName,
            field === 'blockNameFont' ? String(value) : block.blockNameFont,
            field === 'blockNameSize' ? Number(value) : block.blockNameSize,
            field === 'blockNameWeight' ? Number(value) : block.blockNameWeight,
            field === 'blockNameColor' ? String(value) : block.blockNameColor,
            field === 'numColumns' ? Number(value) : block.numColumns,
            field === 'distanceBelowBlockName' ? Number(value) : block.distanceBelowBlockName,
            field === 'labelValueGap' ? Number(value) : block.labelValueGap,
            field === 'marginColumn' ? Number(value) : (block.marginColumn ?? 45),
            field === 'edgeDistance' ? Number(value) : (block.edgeDistance ?? 0),
            field === 'distanceBetweenRows' ? Number(value) : block.distanceBetweenRows,
            field === 'distanceFromPreviousBlock' ? Number(value) : block.distanceFromPreviousBlock,
            field === 'isSongBlock' ? Boolean(value) : block.isSongBlock,
            field === 'isSingleCell' ? Boolean(value) : block.isSingleCell,
            field === 'isCreditsList' ? Boolean(value) : block.isCreditsList,
            field === 'separator' ? String(value) : block.separator,
            field === 'isLogoBlock' ? Boolean(value) : block.isLogoBlock,
            field === 'labelFontName' ? String(value) : block.labelFontName,
            field === 'labelFontSize' ? Number(value) : block.labelFontSize,
            field === 'labelFontWeight' ? Number(value) : block.labelFontWeight,
            field === 'labelAlign' ? String(value) : block.labelAlign,
            field === 'labelVerticalAlign' ? String(value) : block.labelVerticalAlign,
            field === 'labelFontColor' ? String(value) : block.labelFontColor,
            newValueFontName,
            newValueFontSize,
            newValueFontWeight,
            newValueAlign,
            newValueVerticalAlign,
            newValueFontColor,
            newHasSeparateValueFormatting,
            block.dataRows // CRITICAL: Preserve dataRows
          );
          return newBlock;
        }
        return block;
      });
      console.log('BlockConfigForm: handleBlockChange, updatedBlocks:', updatedBlocks);
      return updatedBlocks;
    });
  };

  // Toggle pane collapsed state
  const togglePane = (pane: string) => {
    setCollapsedPanes({
      ...collapsedPanes,
      [pane]: !collapsedPanes[pane]
    });
  };

  // Get the active block
  const activeBlock = blocks[activeBlockIndex];

  return (
    <div className="block-config-form">
      {/* Block Layout Properties */}
      <div className={`config-pane ${collapsedPanes.layout ? 'collapsed' : ''}`}>
        <h3 className="pane-title" onClick={() => togglePane('layout')}>
          {t`Block Layout`}
        </h3>
        <div className="pane-content">
          <div className="form-group">
            <label>{t`Display Block Name`}</label>
            <div className="input-wrapper">
              <input
                type="checkbox"
                checked={activeBlock?.shouldDisplayBlockName}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'shouldDisplayBlockName', e.target.checked)}
              />
            </div>
          </div>
          {/* block name font */}
          <div className="form-group">
            <label>{t`Block Name Font`}</label>
            <div className="input-wrapper">
              <Select
                className="dropdown-menu-select"
                value={activeBlock?.blockNameFont || ""}
                onChange={(e) =>
                  handleBlockChange(activeBlockIndex, "blockNameFont", e.target.value)
                }
                MenuProps={{
                  PaperProps: { className: "block-font-menu" }
                }}
              >
                {fontOptions.map((font) => (
                  <MenuItem
                    key={font.value}
                    value={font.value}
                    className="block-font-option"
                    style={{ fontFamily: font.value }}
                  >
                    {font.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Block Name Font Size`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="6"
                max="72"
                value={activeBlock?.blockNameSize}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'blockNameSize', parseInt(e.target.value))}
              />
              <span className="unit">px</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Block Name Font Weight`}</label>
            <div className="input-wrapper">
              <Select
                className="dropdown-menu-select"
                value={activeBlock?.blockNameWeight ?? 400} // default to Normal
                onChange={(e) =>
                  handleBlockChange(
                    activeBlockIndex,
                    'blockNameWeight',
                    parseInt(e.target.value as string)
                  )
                }
                style={{ width: '92px', height: '34px' }}
              >
                <MenuItem value={400}>{t`Normal`}</MenuItem>
                <MenuItem value={700}>{t`Bold`}</MenuItem>
                <MenuItem value={900}>{t`Bolder`}</MenuItem>
              </Select>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Block Name Font Color`}</label>
            <div className="input-wrapper color-input-wrapper">
              <ColorPicker
                value={activeBlock?.blockNameColor ?? '#ffffff'}
                onChange={(color) => handleBlockChange(activeBlockIndex, 'blockNameColor', color)}
                showValue
              />
            </div>
          </div>
          <div className="form-group">
            <label>{t`Number of Columns`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="1"
                value={activeBlock?.numColumns}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'numColumns', parseInt(e.target.value))}
              />
            </div>
          </div>
            <div className="form-group">
              <label>{t`Distance Below Block Name`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.distanceBelowBlockName}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceBelowBlockName', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>
          {!activeBlock?.isLogoBlock && (
            <div className="form-group">
              <label>{t`Label/Value Gap`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.labelValueGap}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'labelValueGap', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>
          )}
          {activeBlock?.numColumns > 1 && !activeBlock?.isLogoBlock && (
            <div className="form-group">
              <label>{t`Distance Between Columns`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.marginColumn ?? 45}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'marginColumn', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label>{t`Distance Between Rows`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="0"
                value={activeBlock?.distanceBetweenRows}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceBetweenRows', parseInt(e.target.value))}
              />
              <span className="unit">px</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Distance from Previous Block`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="0"
                value={activeBlock?.distanceFromPreviousBlock}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceFromPreviousBlock', parseInt(e.target.value))}
              />
              <span className="unit">px</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Song Block`}</label>
            <div className="input-wrapper">
              <input
                type="checkbox"
                checked={activeBlock?.isSongBlock}
                onChange={(e) => {
                  handleBlockChange(activeBlockIndex, 'isSongBlock', e.target.checked);
                  // When song block is checked, force single cell to be checked
                  if (e.target.checked) {
                    handleBlockChange(activeBlockIndex, 'isSingleCell', true);
                  }
                  if (!e.target.checked) {
                    handleBlockChange(activeBlockIndex, 'isSingleCell', false); // Uncheck single cell if credits list is unchecked
                  }
                }}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Single Cell`}</label>
            <div className="input-wrapper">
              <input
                type="checkbox"
                checked={activeBlock?.isSingleCell}
                disabled={activeBlock?.isSongBlock || activeBlock?.isCreditsList} // Disable if song block or credits list is checked
                onChange={(e) => handleBlockChange(activeBlockIndex, 'isSingleCell', e.target.checked)}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Credits List`}</label>
            <div className="input-wrapper">
              <input
                type="checkbox"
                checked={activeBlock?.isCreditsList}
                onChange={(e) => {
                  handleBlockChange(activeBlockIndex, 'isCreditsList', e.target.checked);
                  if (e.target.checked) {
                    handleBlockChange(activeBlockIndex, 'isSingleCell', true);
                  } else {
                    handleBlockChange(activeBlockIndex, 'isSingleCell', false);
                  }
                }}
              />
              <div
                className={`credits-list-popup ${
                  activeBlock?.isCreditsList ? 'visible' : 'hidden'
                }`}
              >
                <Select
                  className="dropdown-menu-select"
                  value={activeBlock?.separator || '•'}
                  onChange={(e) =>
                    handleBlockChange(activeBlockIndex, 'separator', e.target.value as string)
                  }
                  style={{ width: '80px', height: '34px', marginLeft: '10px' }}
                >
                  <MenuItem value="|">|</MenuItem>
                  <MenuItem value="•">•</MenuItem>
                  <MenuItem value=",">,</MenuItem>
                  <MenuItem value="-">-</MenuItem>
                  <MenuItem value="/">/</MenuItem>
                </Select>
              </div>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Logo Block`}</label>
            <div className="input-wrapper">
              <input
                type="checkbox"
                checked={activeBlock?.isLogoBlock}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'isLogoBlock', e.target.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* Label and Value Formatting (when NOT Logo Block) */}
      {!activeBlock?.isLogoBlock && (
      <>
      <div className={`config-pane ${collapsedPanes.label ? 'collapsed' : ''}`}>
        <h3 className="pane-title" onClick={() => togglePane('label')}>
          {t`Label Formatting`}
        </h3>
        <div className="pane-content">
          <div className="form-group">
            <label>{t`Font`}</label>
            <div className="input-wrapper">
              <Select
                className="dropdown-menu-select"
                value={activeBlock?.labelFontName || ""}
                onChange={(e) =>
                  handleBlockChange(activeBlockIndex, "labelFontName", e.target.value)
                }
                MenuProps={{
                  PaperProps: { className: "block-font-menu" }
                }}
              >
                {fontOptions.map((font) => (
                  <MenuItem
                    key={font.value}
                    value={font.value}
                    className="block-font-option"
                    style={{ fontFamily: font.value }}
                  >
                    {font.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Font Size`}</label>
            <div className="input-wrapper">
              <input
                type="number"
                min="6"
                max="72"
                value={activeBlock?.labelFontSize}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'labelFontSize', parseInt(e.target.value))}
              />
              <span className="unit">px</span>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Block Name Font Weight`}</label>
            <div className="input-wrapper">
              <Select
                className="dropdown-menu-select"
                value={activeBlock?.labelFontWeight ?? 400} // default to Normal
                onChange={(e) =>
                  handleBlockChange(
                    activeBlockIndex,
                    'labelFontWeight',
                    parseInt(e.target.value as string)
                  )
                }
                style={{ width: '92px', height: '34px' }}
              >
                <MenuItem value={400}>{t`Normal`}</MenuItem>
                <MenuItem value={700}>{t`Bold`}</MenuItem>
                <MenuItem value={900}>{t`Bolder`}</MenuItem>
              </Select>
            </div>
          </div>

          <div className="form-group">
            <label>{t`Alignment`}</label>
            <div className="input-wrapper">
              <Select
                className="block-align-select"
                value={activeBlock?.labelAlign ?? 'right'}
                onChange={(e) => handleBlockChange(activeBlockIndex, 'labelAlign', e.target.value)}
                sx={{ 
                  fontSize: '14px', 
                  width: '92px', 
                  height: '34px', 
                  '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                  '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                }}
              >
                <MenuItem value="left">{t`Left`}</MenuItem>
                <MenuItem value="center">{t`Center`}</MenuItem>
                <MenuItem value="right">{t`Right`}</MenuItem>
              </Select>
            </div>
          </div>

          <div className="form-group">
            <label>{t`Vertical Align`}</label>
            <div className="input-wrapper">
              <Select
                className="block-align-select"
                value={activeBlock?.labelVerticalAlign ?? "bottom"}
                onChange={(e) => handleBlockChange(activeBlockIndex, "labelVerticalAlign", e.target.value)}
                sx={{ 
                  fontSize: '14px', 
                  width: '92px', 
                  height: '34px', 
                  '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                  '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                }}
              >
                <MenuItem value="top">{t`Top`}</MenuItem>
                <MenuItem value="center">{t`Center`}</MenuItem>
                <MenuItem value="bottom">{t`Bottom`}</MenuItem>
              </Select>
            </div>
          </div>
          
          <div className="form-group">
            <label>{t`Font Color`}</label>
            <div className="input-wrapper color-input-wrapper">
              <ColorPicker
                value={activeBlock?.labelFontColor ?? '#ffffff'}
                onChange={(color) => handleBlockChange(activeBlockIndex, 'labelFontColor', color)}
                showValue
              />
            </div>
          </div>
        </div>
      </div>

      {/* Value Formatting Properties */}
      <div className={`config-pane ${collapsedPanes.value ? 'collapsed' : ''}`}>
        <h3 className="pane-title" onClick={() => togglePane('value')}>
          {t`Value Formatting`}
        </h3>
        <div className="pane-content">
          {/* Toggle for separate value formatting */}
          <div className="form-group"style={{ marginLeft: "-11px", display: "flex",flexDirection: "row",alignItems: "center",whiteSpace: "nowrap",}}>
          <label style={{ marginLeft: "12px" }}>{t`Separate formatting for values?`}</label>
          <input
            type="checkbox"
            style={{ marginLeft: "8px" }}
            checked={activeBlock?.hasSeparateValueFormatting}
            onChange={(e) =>
              handleBlockChange(activeBlockIndex, 'hasSeparateValueFormatting', e.target.checked)
            }
          />
        </div>
          
          {/* Value formatting options - only show if separate formatting is enabled */}
          {activeBlock?.hasSeparateValueFormatting && (
            <>
              <div className="form-group">
                <label>{t`Font`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.valueFontName || ""}
                    onChange={(e) =>
                      handleBlockChange(activeBlockIndex, "valueFontName", e.target.value)
                    }
                    MenuProps={{
                      PaperProps: { className: "block-font-menu" }
                    }}
                  >
                    {fontOptions.map((font) => (
                      <MenuItem
                        key={font.value}
                        value={font.value}
                        className="block-font-option"
                        style={{ fontFamily: font.value }}
                      >
                        {font.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              
              <div className="form-group">
                <label>{t`Font Size`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="6"
                    max="72"
                    value={activeBlock?.valueFontSize}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'valueFontSize', parseInt(e.target.value))}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
              
              <div className="form-group">
                <label>{t`Font Weight`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.valueFontWeight ?? 400} // default to Normal
                    onChange={(e) =>
                      handleBlockChange(
                        activeBlockIndex,
                        'valueFontWeight',
                        parseInt(e.target.value as string)
                      )
                    }
                    style={{ width: '92px', height: '34px' }}
                  >
                    <MenuItem value={400}>{t`Normal`}</MenuItem>
                    <MenuItem value={700}>{t`Bold`}</MenuItem>
                    <MenuItem value={900}>{t`Bolder`}</MenuItem>
                  </Select>
                </div>
              </div>

              <div className="form-group">
                <label>{t`Alignment`}</label>
                <div className="input-wrapper">
                  <Select
                    className="block-align-select"
                    value={activeBlock?.valueAlign ?? 'right'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'valueAlign', e.target.value)}
                    sx={{ 
                      fontSize: '14px',
                      width: '92px',
                      height: '34px',
                      '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                      '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                    }}
                    MenuProps={{
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      PaperProps: { style: { marginTop: '18px' } }
                    }}
                  >
                    <MenuItem value="left">{t`Left`}</MenuItem>
                    <MenuItem value="center">{t`Center`}</MenuItem>
                    <MenuItem value="right">{t`Right`}</MenuItem>
                  </Select>
                </div>
              </div>

              <div className="form-group">
                <label>{t`Vertical Align`}</label>
                <div className="input-wrapper">
                  <Select
                    className="block-align-select"
                    value={activeBlock?.valueVerticalAlign ?? "bottom"}
                    onChange={(e) => handleBlockChange(activeBlockIndex, "valueVerticalAlign", e.target.value)}
                    sx={{ 
                      fontSize: '14px', 
                      width: '92px', 
                      height: '34px', 
                      '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                      '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                    }}
                  >
                    <MenuItem value="top">{t`Top`}</MenuItem>
                    <MenuItem value="center">{t`Center`}</MenuItem>
                    <MenuItem value="bottom">{t`Bottom`}</MenuItem>
                  </Select>
                </div>
              </div>
              
              <div className="form-group">
                <label>{t`Font Color`}</label>
                <div className="input-wrapper color-input-wrapper">
                  <ColorPicker
                    value={activeBlock?.valueFontColor ?? '#ffffff'}
                    onChange={(color) => handleBlockChange(activeBlockIndex, 'valueFontColor', color)}
                    showValue
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default BlockConfigForm;